/**
 * `/preload` — the Preload viewer/editor (Phase 3c).
 *
 * Shows what each of the three PAs is preloaded with:
 *   • Cloud PA — the staff-instructions code default (view; live override on /config).
 *   • Deep worker — CLAUDE.md + SKILL.md: baseline (git mirror) + live DB override,
 *     EDITABLE inline via `set_agent_preload` (guard-checked).
 *   • Builder PA — view-only note (its preload is the build-protocol CLAUDE.md +
 *     briefing; edited via git / the builder chat).
 *
 * The worker card leads with its RUNNER state, because saving an override and
 * the override taking effect are two different events: the save writes a
 * `pa_agent_preload` row, and the laptop runner applies it at the top of its
 * next tick. With the launchd PA fleet stood down (since 2026-07-19) that tick
 * never comes, so the page said "goes live on the next hourly tick" over an
 * editor that was, in practice, writing to a drawer. The state is derived from
 * the `layer-pa-worker` heartbeat rather than hardcoded here — see
 * `repositories/preload.ts` for why that distinction matters.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  type PreloadResponse,
  type PreloadRunner,
  type PreloadWorkerFile,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import {
  AgentPreloadEditor,
  ReadOnlyBlock,
} from "../components/AgentPreloadEditor";

export function Preload(): JSX.Element {
  const [data, setData] = useState<PreloadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const reload = useCallback(async (): Promise<void> => {
    const r = await api.preload();
    setData(r);
    setError(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .preload()
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? (e.code ?? `http_${e.status}`) : e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <p className="empty">Loading preload…</p>;
  if (error !== null) return <p className="empty">Couldn&apos;t load preload ({error}).</p>;
  if (data === null) return <p className="empty">No data.</p>;

  return (
    <div>
      <h1 className="page-title">Preload</h1>
      <p className="subtle">
        What each PA is preloaded with. The deep worker&apos;s files are editable; a
        saved override is applied by the laptop runner on its next tick.
      </p>

      <section className="panel" style={panelStyle}>
        <h2 className="section-title">{data.worker.label}</h2>
        <p style={hintStyle}>Editable · override &gt; git default</p>
        <RunnerBanner runner={data.worker.runner} />
        {data.worker.files.map((f) => (
          <AgentPreloadEditor key={f.file} agent="worker" file={f} onSaved={reload} />
        ))}
      </section>

      <section className="panel" style={panelStyle}>
        <h2 className="section-title">{data.cloud.label}</h2>
        <p style={hintStyle}>{data.cloud.edit_hint}</p>
        <p style={{ margin: "0 0 8px" }}>
          <Link to="/config" className="data-member-link">
            Edit the live override on /config →
          </Link>
        </p>
        <ReadOnlyBlock label="staff_instructions (code default)" text={data.cloud.baseline} />
      </section>

      <section className="panel" style={panelStyle}>
        <h2 className="section-title">{data.builder.label}</h2>
        <p style={hintStyle}>{data.builder.note}</p>
      </section>
    </div>
  );
}

/**
 * The worker card's headline: is anything going to READ what you save here?
 *
 * `ok` gets a one-line confirmation. Anything else gets a warning that says
 * plainly what will and won't happen — the edit is stored, the worker does not
 * see it yet — and links to the `/status` tile that owns the verdict, so the
 * operator can act on the runner rather than re-saving the file.
 */
function RunnerBanner({ runner }: { readonly runner: PreloadRunner }): JSX.Element {
  const last =
    runner.last_tick_at === null
      ? "never"
      : runner.last_tick_at.slice(0, 16).replace("T", " ");

  if (runner.state === "ok") {
    return (
      <p style={{ ...hintStyle, marginBottom: 12 }}>
        Runner ticking — last cycle {last}
        {runner.ok === false ? " (reported a failed operator tick)" : ""}. A saved
        override is applied on the next tick.
      </p>
    );
  }

  return (
    <div
      style={{
        border: "1px solid var(--warn-border, #f59e0b)",
        background: "var(--warn-bg, rgba(245,158,11,0.10))",
        borderRadius: 6,
        padding: "10px 12px",
        margin: "0 0 14px",
        fontSize: "0.88em",
      }}
    >
      <strong>The deep-worker runner is not ticking.</strong>{" "}
      {runner.state === "never"
        ? "It has never reported a completed cycle for this studio."
        : `Its last completed cycle was ${last}` +
          (runner.stale_after_hours !== null
            ? `, past the ${runner.stale_after_hours}h freshness window.`
            : ".")}{" "}
      Edits below still SAVE — the override is stored and will be applied the
      first time the runner ticks again — but the worker is not reading them
      today. Re-enable the launchd agent (<code>ai.layer-pa</code>) to make them
      take effect;{" "}
      <Link to="/status" className="data-member-link">
        /status
      </Link>{" "}
      owns this verdict.
    </div>
  );
}

const panelStyle: React.CSSProperties = { marginBottom: 20, padding: 16 };
const hintStyle: React.CSSProperties = { fontSize: "0.85em", opacity: 0.75, margin: "0 0 10px" };
const codeStyle: React.CSSProperties = {
  fontFamily: "var(--mono, monospace)",
  fontSize: "0.9em",
  background: "var(--code-bg, rgba(127,127,127,0.12))",
  padding: "2px 6px",
  borderRadius: 4,
};
const preStyle: React.CSSProperties = {
  margin: "6px 0 0",
  padding: 10,
  background: "var(--code-bg, rgba(127,127,127,0.12))",
  borderRadius: 6,
  overflowX: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: "0.8em",
  maxHeight: 420,
  overflowY: "auto",
};
const editorStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 300,
  fontFamily: "var(--mono, monospace)",
  fontSize: "0.82em",
  padding: 10,
  boxSizing: "border-box",
};
